// The support assistant's chat panel (spec §11), opened by the headset button in the top bar - as XpeditionGO's
// Ask support page (lib/support/support_page.dart). It stays mounted while closed, so the conversation lasts until
// New conversation or a reload.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Markdown } from './Markdown';
import type { SupportMessage } from './support';
import { askSupport, SupportError } from './support';
import styles from './SupportPanel.module.css';

const SUGGESTIONS = ['plan', 'snap', 'save', 'import'] as const;

export function SupportPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<SupportError['reason'] | null>(null);
  const asking = useRef<AbortController | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  // Keep the latest words in view as the answer arrives.
  useEffect(() => {
    list.current?.scrollTo?.({ top: list.current.scrollHeight });
  }, [messages, error]);

  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const ask = async (question: string) => {
    const text = question.trim();
    if (!text || busy) return;
    const conversation: SupportMessage[] = [...messages, { role: 'user', content: text }];
    setMessages([...conversation, { role: 'assistant', content: '' }]);
    setDraft('');
    setError(null);
    setBusy(true);
    const controller = new AbortController();
    asking.current = controller;
    try {
      await askSupport(
        conversation,
        (fragment) =>
          setMessages((current) => {
            const last = current.at(-1)!;
            return [...current.slice(0, -1), { ...last, content: last.content + fragment }];
          }),
        controller.signal,
      );
    } catch (e) {
      if (controller.signal.aborted) return;
      setError(e instanceof SupportError ? e.reason : 'failed');
      // Without an answer, the question goes too, so the conversation stays question/answer pairs; it can be
      // asked again.
      setMessages((current) => (current.at(-1)?.content ? current : current.slice(0, -2)));
      setDraft((current) => current || text);
    } finally {
      if (asking.current === controller) {
        asking.current = null;
        setBusy(false);
      }
    }
  };

  const startAgain = () => {
    asking.current?.abort();
    asking.current = null;
    setBusy(false);
    setMessages([]);
    setError(null);
    setDraft('');
    input.current?.focus();
  };

  return (
    <aside className={styles.panel} hidden={!open} aria-labelledby="support-title" role="dialog">
      <header className={styles.header}>
        <h2 id="support-title">{t('support.title')}</h2>
        {messages.length > 0 && (
          <button type="button" className={styles.textButton} onClick={startAgain}>
            {t('support.newConversation')}
          </button>
        )}
        <button type="button" className={styles.close} aria-label={t('support.close')} onClick={onClose}>
          ×
        </button>
      </header>

      <div className={styles.messages} ref={list} aria-live="polite">
        {messages.length === 0 && (
          <div className={styles.intro}>
            <p>{t('support.intro')}</p>
            <div className={styles.suggestions}>
              {SUGGESTIONS.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={styles.suggestion}
                  onClick={() => void ask(t(`support.suggestions.${key}`))}
                >
                  {t(`support.suggestions.${key}`)}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? styles.user : styles.assistant}>
            {m.content ? (
              <Answer text={m.content} />
            ) : busy && i === messages.length - 1 ? (
              <Dots label={t('support.thinking')} />
            ) : null}
          </div>
        ))}
        {error && (
          <p className={styles.error} role="alert">
            {t(`support.errors.${error}`)}
          </p>
        )}
      </div>

      <form
        className={styles.composer}
        onSubmit={(event) => {
          event.preventDefault();
          void ask(draft);
        }}
      >
        <label className="visually-hidden" htmlFor="support-question">
          {t('support.placeholder')}
        </label>
        <textarea
          id="support-question"
          ref={input}
          className={styles.input}
          rows={2}
          maxLength={4000}
          value={draft}
          placeholder={t('support.placeholder')}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // Enter sends; Shift+Enter starts a new line.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void ask(draft);
            }
          }}
        />
        <button type="submit" className={styles.send} disabled={busy || !draft.trim()}>
          {t('support.send')}
        </button>
      </form>
      <p className={styles.note}>{t('support.note')}</p>
    </aside>
  );
}

/** The answer's light Markdown (bold button names, lists, steps), as the specification is written. */
const Answer = ({ text }: { text: string }) => <Markdown text={text} />;

const Dots = ({ label }: { label: string }) => (
  <span className={styles.dots} role="status" aria-label={label}>
    <span />
    <span />
    <span />
  </span>
);

export const HeadsetIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
    <path d="M4 14h3v6H5a1 1 0 0 1-1-1v-5zM20 14h-3v6h2a1 1 0 0 0 1-1v-5z" />
    <path d="M17 20c0 1.1-1.8 2-4 2" />
  </svg>
);
